import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import * as Tone from "tone";
import {
  Step,
  ChordProgression,
  PatternGeneration,
  ChordTiming,
  DEFAULT_PATTERN_GENERATION,
  DEFAULT_CHORD_TIMING,
} from "../types";
import {
  rebuildStepVoices,
  applySnapshotToVoice,
  applyStepVoiceMix,
  getStepVoiceSampler,
  getStepVoiceReverseSampler,
  triggerStepVoiceEnvelope,
  triggerStepVoiceEnvelopeAttack,
  getAdsrEnabled,
  type MasterFXSnapshot,
  createReverseStepSampler,
  getSampleDuration,
  // Realtime node reads (capture automation/expression)
  getCurrentFilterCutoffHz,
  getCurrentDelayTime,
  getCurrentDelayFeedback,
  getCurrentDelayMix,
  getCurrentReverbMix,
  getReverseMix,
  // Static fallbacks
  getReverbDecay,
  MIN_FILTER_CUTOFF,
  MAX_FILTER_CUTOFF,
} from "../../../utils/audioUtils";
import { playRomanInKey, getRomanChordNotes } from "../../../utils/chordUtils";
import {
  usePerformanceActions,
  usePerformanceValues,
} from "../../../context/PerformanceContext";
import { Register } from "../../../utils/registerVoicing";
import {
  getPatternDuration,
  getChordDuration,
  getPatternStepTime,
  getChordAtTimeWithBpm,
  getChordIndexAtTime,
  getMasterLoopDuration,
} from "../../../utils/timecodeUtils";
import { expandDroneNotesToRegister } from "../../../utils/droneNotes";

const MAX_GROOVE_OFFSET_SECONDS = 0.05;
const FULL_SAMPLE_PLAYBACK_DURATION_SECONDS = 600;

export const useSequencerLogic = (
  bpm: number,
  stepsCount: number,
  pattern: Step[],
  progression: ChordProgression,
  selectedKey: string,
  sampler: Tone.Sampler | null,
  samplesMap: { [note: string]: string } | null,
  isKeyboardMode: boolean,
  disabled: boolean,
  fxModes?: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  },
  register?: Register,
  sequencerMode?: "chord" | "drone-mono" | "drone-duo",
  droneNotes?: string[],
  // New pattern generation config
  patternGeneration?: PatternGeneration,
  // New chord timing config
  chordTiming?: ChordTiming
) => {
  const [currentStep, setCurrentStep] = useState<number>(-1);
  const [currentChordIndex, setCurrentChordIndex] = useState<number>(-1);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [error] = useState<string | null>(null);

  // Use defaults if not provided - wrapped in useMemo to prevent unnecessary recalculations
  const patternConfig = useMemo(
    () =>
      patternGeneration || {
        ...DEFAULT_PATTERN_GENERATION,
        steps: stepsCount,
      },
    [patternGeneration, stepsCount]
  );
  const chordConfig = useMemo(
    () => chordTiming || DEFAULT_CHORD_TIMING,
    [chordTiming]
  );

  // Track previous chord's top note for voice leading
  const previousTopNoteRef = useRef<number | undefined>(undefined);
  const registerRef = useRef<Register | undefined>(register);
  const setupGenerationRef = useRef<number>(0);
  const warmupPromiseRef = useRef<Promise<void> | null>(null);
  const scheduleReadyRef = useRef<boolean>(false);
  const [isScheduleReady, setIsScheduleReady] = useState<boolean>(false);
  const sampleDurationCacheRef = useRef<Map<string, number>>(new Map());
  const reverseSamplerMapRef = useRef<{ [note: string]: string } | null>(null);

  // Get performance context values/actions
  const { groove } = usePerformanceValues();
  const { getStrum } = usePerformanceActions();

  useEffect(() => {
    registerRef.current = register;
  }, [register]);

  useEffect(() => {
    if (reverseSamplerMapRef.current !== samplesMap) {
      sampleDurationCacheRef.current.clear();
    }
  }, [samplesMap]);

  // Calculate timing values
  const patternDuration = useMemo(
    () => getPatternDuration(patternConfig, bpm),
    [patternConfig, bpm]
  );
  const chordDuration = useMemo(
    () => getChordDuration(chordConfig, bpm),
    [chordConfig, bpm]
  );
  const masterLoopDuration = useMemo(
    () => getMasterLoopDuration(patternConfig, chordConfig, bpm),
    [patternConfig, chordConfig, bpm]
  );

  // Calculate the note subdivision based on pattern config
  // This determines how frequently steps are triggered
  const stepInterval = useMemo(() => {
    // Step interval = pattern duration / number of steps
    return patternDuration / patternConfig.steps;
  }, [patternDuration, patternConfig.steps]);

  const grooveMaxOffset = useMemo(
    () => Math.min(stepInterval * 0.5, MAX_GROOVE_OFFSET_SECONDS),
    [stepInterval]
  );

  // Build per-step voices when samples are provided
  useEffect(() => {
    if (!samplesMap || disabled) return;
    if (Object.keys(samplesMap).length === 0) return;
    // Rebuild all step voices when the instrument/sample set or step count changes
    rebuildStepVoices(samplesMap, patternConfig.steps);
  }, [samplesMap, disabled, patternConfig.steps]);

  // Update the sequence with the new chord progression playback
  useEffect(() => {
    if (!sampler && !samplesMap) return;
    if (disabled) return; // Don't set up sequence if disabled

    const setupGeneration = ++setupGenerationRef.current;
    scheduleReadyRef.current = false;
    setIsScheduleReady(false);

    // Clear any existing sequences
    Tone.Transport.cancel(0);

    console.log(
      `Setting up timecode-based sequence with ${patternConfig.steps} steps, ${patternConfig.bars} bars, ${patternConfig.rate}x rate`
    );
    console.log(
      `Pattern duration: ${patternDuration.toFixed(
        3
      )}s, Chord duration: ${chordDuration.toFixed(3)}s`
    );

    // Calculate how many pattern loops fit in the master duration
    const patternLoops = Math.ceil(masterLoopDuration / patternDuration);
    const totalSteps = patternLoops * patternConfig.steps;

    // Schedule all steps for the master loop
    const scheduledEvents: number[] = [];
    let isCancelled = false;
    let scheduledTopNote: number | undefined = previousTopNoteRef.current;

    const clampMix = (value: number): number => Math.max(0, Math.min(1, value));

    const resolveReverseMix = (stepFXMixes?: Step["fxMixes"]): number => {
      if (fxModes?.reverse && stepFXMixes?.reverse !== undefined) {
        return clampMix(stepFXMixes.reverse);
      }
      return clampMix(getReverseMix());
    };

    const getReverseGains = (reverseMixValue: number) => {
      const reverseMixClamped = clampMix(reverseMixValue);
      const reverseGain = reverseMixClamped <= 0.5 ? reverseMixClamped * 2 : 1;
      const regularGain =
        reverseMixClamped <= 0.5 ? 1 : (1 - reverseMixClamped) * 2;
      return { regularGain, reverseGain };
    };

    const registerSnapshot = registerRef.current;
    const expandedDroneNotes =
      droneNotes && droneNotes.length > 0
        ? expandDroneNotesToRegister(droneNotes, registerSnapshot)
        : [];

    const scheduleEvents = async () => {
      if (samplesMap && Object.keys(samplesMap).length > 0) {
        if (reverseSamplerMapRef.current !== samplesMap) {
          await createReverseStepSampler(samplesMap);
          reverseSamplerMapRef.current = samplesMap;
        }
        if (isCancelled || setupGenerationRef.current !== setupGeneration) {
          return;
        }
      }

      const noteDurationCache = sampleDurationCacheRef.current;

      const scheduledStepTimings = Array.from({ length: totalSteps }, (_, absoluteStep) => {
        const stepInPattern = absoluteStep % patternConfig.steps;
        const loopIndex = Math.floor(absoluteStep / patternConfig.steps);
        const stepTimeInPattern = getPatternStepTime(
          stepInPattern,
          patternConfig,
          bpm
        );
        const absoluteTime = loopIndex * patternDuration + stepTimeInPattern;
        const grooveOffset =
          stepInPattern % 2 === 0 ? groove * grooveMaxOffset : 0;
        const grooveAdjustedTime = absoluteTime - grooveOffset;
        const normalizedGrooveTime =
          ((grooveAdjustedTime % masterLoopDuration) + masterLoopDuration) %
          masterLoopDuration;

        return {
          absoluteStep,
          stepInPattern,
          absoluteTime,
          grooveAdjustedTime,
          normalizedGrooveTime,
        };
      }).filter((stepTiming) => stepTiming.absoluteTime < masterLoopDuration);

      const nextActiveHoldDurationByAbsoluteStep = new Map<number, number>();
      const activeStepTimings = scheduledStepTimings.filter((stepTiming) => {
        const step = pattern[stepTiming.stepInPattern];
        return !!step?.active;
      });

      activeStepTimings.forEach((currentStepTiming, index) => {
        const nextStepTiming = activeStepTimings[(index + 1) % activeStepTimings.length];
        if (!nextStepTiming) return;

        let holdDuration =
          nextStepTiming.grooveAdjustedTime - currentStepTiming.grooveAdjustedTime;

        if (holdDuration <= 0) {
          holdDuration += masterLoopDuration;
        }

        nextActiveHoldDurationByAbsoluteStep.set(
          currentStepTiming.absoluteStep,
          Math.max(0.001, holdDuration || stepInterval)
        );
      });

      for (const stepTiming of scheduledStepTimings) {
        if (isCancelled) return;

        const {
          absoluteStep,
          stepInPattern,
          absoluteTime,
          grooveAdjustedTime,
          normalizedGrooveTime,
        } = stepTiming;

        const currentStepObj = pattern[stepInPattern];
        const stepFXMixes = currentStepObj?.fxMixes;

        let reverseNotes: string[] = [];
        if (currentStepObj?.active) {
          if (currentStepObj.mode === "chord" && (sampler || samplesMap)) {
            const roman = getChordAtTimeWithBpm(
              absoluteTime,
              chordConfig,
              progression.roman,
              bpm
            );
            const chordNotes = getRomanChordNotes({
              roman,
              key: selectedKey,
              mode: progression.mode,
              register: registerSnapshot,
              previousTopNote: scheduledTopNote,
            });
            reverseNotes = chordNotes.notes;
            if (chordNotes.topNote !== undefined) {
              scheduledTopNote = chordNotes.topNote;
            }
          } else if (
            currentStepObj.mode === "drone" &&
            droneNotes &&
            droneNotes.length > 0
          ) {
            reverseNotes = expandedDroneNotes;
          }
        }

        if (reverseNotes.length > 0 && samplesMap) {
          const durationSampler = getStepVoiceSampler(stepInPattern);

          for (const note of reverseNotes) {
            if (isCancelled) return;
            const cachedDuration = noteDurationCache.get(note);
            let sampleDuration =
              cachedDuration ??
              (durationSampler
                ? await getSampleDuration(durationSampler, note)
                : 0);

            if (!sampleDuration || sampleDuration <= 0) {
              sampleDuration = stepInterval;
            }

            if (cachedDuration === undefined) {
              noteDurationCache.set(note, sampleDuration);
            }

            const reverseStartTime = grooveAdjustedTime - sampleDuration;
            const normalizedReverseTime =
              ((reverseStartTime % masterLoopDuration) + masterLoopDuration) %
              masterLoopDuration;

            const reverseEventId = Tone.Transport.schedule((time) => {
              if (!currentStepObj?.active) return;

              const reverseMixValue = resolveReverseMix(stepFXMixes);
              if (reverseMixValue <= 0) return;

              const { regularGain, reverseGain } =
                getReverseGains(reverseMixValue);
              applyStepVoiceMix(stepInPattern, regularGain, reverseGain, time);

              const reverseSampler = getStepVoiceReverseSampler(stepInPattern);
              if (!reverseSampler) return;

              try {
                reverseSampler.triggerAttackRelease(
                  note,
                  sampleDuration,
                  time,
                  0.7
                );
                triggerStepVoiceEnvelopeAttack(stepInPattern, time);
              } catch (err) {
                console.error(`Failed to play reverse note ${note}:`, err);
              }
            }, normalizedReverseTime);

            scheduledEvents.push(reverseEventId);
          }
        }

        const eventId = Tone.Transport.schedule((time) => {
          // Route UI updates through Tone.Draw so React rendering doesn't block
          // timing-critical audio scheduling callbacks.
          Tone.Draw.schedule(() => {
            setCurrentStep(stepInPattern);
            const chordIdx = getChordIndexAtTime(absoluteTime, chordConfig, bpm);
            setCurrentChordIndex(chordIdx);
          }, time);

          if (!currentStepObj) return;

          const adsrEnabled = getAdsrEnabled();

          // Trigger sound if this step is active
          if (currentStepObj.active) {
            const nextChordHoldDurationSeconds =
              nextActiveHoldDurationByAbsoluteStep.get(absoluteStep) ??
              Math.max(stepInterval, 0.001);
            const samplerDurationSeconds = adsrEnabled
              ? FULL_SAMPLE_PLAYBACK_DURATION_SECONDS
              : nextChordHoldDurationSeconds;

            // Resolve FX values: use per-step if in per-step mode and step has value, otherwise use global
            const resolveFilterCutoff = (): number => {
              if (fxModes?.filter && stepFXMixes?.filter !== undefined) {
                return Math.max(
                  MIN_FILTER_CUTOFF,
                  Math.min(MAX_FILTER_CUTOFF, stepFXMixes.filter)
                );
              }
              return getCurrentFilterCutoffHz();
            };

            const resolveDelayMix = (): number => {
              if (fxModes?.delay && stepFXMixes?.delay !== undefined) {
                return Math.max(0, Math.min(1, stepFXMixes.delay));
              }
              return getCurrentDelayMix();
            };

            const resolveReverbMix = (): number => {
              if (fxModes?.reverb && stepFXMixes?.reverb !== undefined) {
                return Math.max(0, Math.min(1, stepFXMixes.reverb));
              }
              return getCurrentReverbMix();
            };

            const reverseMixValue = resolveReverseMix(stepFXMixes);
            const { regularGain, reverseGain } =
              getReverseGains(reverseMixValue);

            const snapshot: MasterFXSnapshot = {
              filterCutoffHz: resolveFilterCutoff(),
              delayTime: getCurrentDelayTime(),
              delayFeedback: getCurrentDelayFeedback(),
              delayMix: resolveDelayMix(),
              reverbDecay: getReverbDecay(),
              reverbMix: resolveReverbMix(),
            };

            // Apply to this step's voice index
            applySnapshotToVoice(stepInPattern, snapshot, time);
            applyStepVoiceMix(stepInPattern, regularGain, reverseGain, time);

            if (currentStepObj.mode === "chord" && (sampler || samplesMap)) {
              // Use timecode-based chord lookup
              const roman = getChordAtTimeWithBpm(
                absoluteTime,
                chordConfig,
                progression.roman,
                bpm
              );

              if (samplesMap) {
                const voiceSampler = getStepVoiceSampler(stepInPattern);
                if (voiceSampler) {
                  const topNote = playRomanInKey(voiceSampler as any, {
                    roman,
                    key: selectedKey,
                    mode: progression.mode,
                    duration: samplerDurationSeconds,
                    time,
                    velocity: 0.7,
                    strum: getStrum(),
                    register: registerRef.current,
                    previousTopNote: previousTopNoteRef.current,
                  });
                  if (topNote !== undefined) {
                    previousTopNoteRef.current = topNote;
                  }
                  triggerStepVoiceEnvelope(
                    stepInPattern,
                    nextChordHoldDurationSeconds,
                    time
                  );
                }
              } else if (sampler) {
                const topNote = playRomanInKey(sampler, {
                  roman,
                  key: selectedKey,
                  mode: progression.mode,
                  duration: samplerDurationSeconds,
                  time,
                  velocity: 0.7,
                  strum: getStrum(),
                  register: registerRef.current,
                  previousTopNote: previousTopNoteRef.current,
                });
                if (topNote !== undefined) {
                  previousTopNoteRef.current = topNote;
                }
              }
            } else if (
              currentStepObj.mode === "drone" &&
              droneNotes &&
              droneNotes.length > 0 &&
              (sampler || samplesMap)
            ) {
              const dronePlaybackNotes = expandedDroneNotes;
              if (samplesMap) {
                const voiceSampler = getStepVoiceSampler(stepInPattern);
                if (voiceSampler) {
                  dronePlaybackNotes.forEach((note) => {
                    try {
                      voiceSampler.triggerAttackRelease(
                        note,
                        samplerDurationSeconds,
                        time,
                        0.7
                      );
                    } catch (err) {
                      console.error(`Failed to play drone note ${note}:`, err);
                    }
                  });
                  triggerStepVoiceEnvelope(
                    stepInPattern,
                    nextChordHoldDurationSeconds,
                    time
                  );
                }
              } else if (sampler) {
                dronePlaybackNotes.forEach((note) => {
                  try {
                    sampler.triggerAttackRelease(
                      note,
                      samplerDurationSeconds,
                      time,
                      0.7
                    );
                  } catch (err) {
                    console.error(`Failed to play drone note ${note}:`, err);
                  }
                });
              }
            }
          }
        }, normalizedGrooveTime);

        scheduledEvents.push(eventId);
      }

      // Set up loop at master duration
      Tone.Transport.loop = true;
      Tone.Transport.loopStart = 0;
      Tone.Transport.loopEnd = masterLoopDuration;

      console.log(`Master loop duration: ${masterLoopDuration.toFixed(3)}s`);

      if (!isCancelled && setupGenerationRef.current === setupGeneration) {
        scheduleReadyRef.current = true;
        setIsScheduleReady(true);
      }
    };

    const warmupPromise = scheduleEvents().catch((err) => {
      console.error("Error during sequencer warmup/scheduling:", err);
      if (!isCancelled && setupGenerationRef.current === setupGeneration) {
        scheduleReadyRef.current = false;
        setIsScheduleReady(false);
      }
    });
    warmupPromiseRef.current = warmupPromise;

    // Clean up scheduled events when dependencies change
    return () => {
      isCancelled = true;
      scheduleReadyRef.current = false;
      setIsScheduleReady(false);
      scheduledEvents.forEach((id) => Tone.Transport.clear(id));
      Tone.Transport.loop = false;
    };
  }, [
    sampler,
    samplesMap,
    pattern,
    patternConfig,
    chordConfig,
    progression,
    selectedKey,
    isKeyboardMode,
    disabled,
    groove,
    fxModes,
    sequencerMode,
    droneNotes,
    bpm,
    patternDuration,
    chordDuration,
    masterLoopDuration,
    stepInterval,
    grooveMaxOffset,
    getStrum,
  ]);

  const prepareSequencer = useCallback(async (): Promise<boolean> => {
    if (disabled || (!sampler && !samplesMap)) {
      return false;
    }

    const warmupPromise = warmupPromiseRef.current;
    if (warmupPromise) {
      await warmupPromise;
    }

    return scheduleReadyRef.current;
  }, [disabled, sampler, samplesMap]);

  // Reset previous top note when progression or register changes
  useEffect(() => {
    previousTopNoteRef.current = undefined;
  }, [progression, register]);

  // Start the sequencer
  const startSequencer = useCallback(async () => {
    console.log("Starting sequencer");
    const isReady = await prepareSequencer();
    if (!isReady) {
      console.warn("Sequencer start blocked: scheduling warmup not ready");
      return;
    }
    Tone.Transport.position = 0;
    Tone.Transport.start();
    setIsPlaying(true);
  }, [prepareSequencer]);

  // Stop the sequencer
  const stopSequencer = useCallback(() => {
    console.log("Stopping sequencer");
    Tone.Transport.pause();
    setIsPlaying(false);
    setCurrentStep(-1);
    setCurrentChordIndex(-1);
  }, []);

  // Handle BPM changes
  useEffect(() => {
    Tone.Transport.bpm.value = bpm;
  }, [bpm]);

  return {
    currentStep,
    currentChordIndex,
    isPlaying,
    isScheduleReady,
    error,
    startSequencer,
    stopSequencer,
    prepareSequencer,
    // Expose timing info for potential visualization
    patternDuration,
    chordDuration,
    masterLoopDuration,
    stepInterval,
  };
};
